/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Button, Text } from '@/shared/ui';
import { useSettingsDirty } from '../hooks/use-settings-dirty';

/**
 * The save bar, pinned to the bottom of the content area.
 *
 * Present only while the active section has an unsaved change, so it is not
 * a permanent strip of chrome the eye learns to skip — when it appears, it
 * means something.
 *
 * @returns The bar, or null when there is nothing to save.
 */
export function SettingsSaveBar() {
  const { t } = useTranslation('features/agency-settings');
  const { isDirty, isSaving, save, discard } = useSettingsDirty();
  if (!isDirty) return null;

  return (
    <div
      data-testid="settings-save-bar"
      style={{
        position: 'sticky',
        bottom: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 'var(--space-4)', flexWrap: 'wrap',
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--surface-raised)',
        borderTop: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-md)',
      }}
    >
      <Text style={{ margin: 0, fontSize: 'var(--text-body-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
        {t('v2.saveBar.unsaved')}
      </Text>
      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        {/* Neither label may wrap to a second line: a two-line button in a
            bar this short reads as a broken layout. */}
        <Button
          variant="outlined"
          size="sm"
          onClick={discard}
          disabled={isSaving}
          data-testid="settings-save-bar-discard"
          style={{ whiteSpace: 'nowrap' }}
        >
          {t('v2.saveBar.discard')}
        </Button>
        <Button
          size="sm"
          onClick={save}
          loading={isSaving}
          disabled={isSaving}
          data-testid="settings-save-bar-save"
          style={{ whiteSpace: 'nowrap' }}
        >
          {t('v2.saveBar.save')}
        </Button>
      </div>
    </div>
  );
}
